import { PublicClientApplication } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import * as React from 'react'
import { flushSync } from 'react-dom'
import ReactDOM from 'react-dom/client'
import { registerApplication, start } from 'single-spa'

import { App } from './app/App'
import { msalConfig, tokenRequest } from './auth/config'
import { hasAnyRole } from './auth/roles'
import { getRolesFromToken } from './auth/tokenUtils'
import { microfrontends } from './microfrontends'
import './styles.scss'

const appMsalInstance = new PublicClientApplication(msalConfig)

declare const System: { import: (name: string) => Promise<any> }

async function bootstrap() {
  await appMsalInstance.initialize()

  // ✅ Dynamically inject child app imports
  const importMap = { imports: {} as Record<string, string> }
  microfrontends.forEach(app => {
    importMap.imports[app.name] = app.url
  })

  const script = document.createElement('script')
  script.type = 'systemjs-importmap'
  script.textContent = JSON.stringify(importMap)
  document.head.appendChild(script)

  // Get token dynamically — fetch fresh accounts each time (not stale closure)
  const getToken = async (): Promise<string> => {
    const accounts = appMsalInstance.getAllAccounts()
    if (accounts.length === 0) return ''
    try {
      const response = await appMsalInstance.acquireTokenSilent({
        ...tokenRequest,
        account: accounts[0],
      })
      return response.accessToken
    } catch {
      return ''
    }
  }

  // ✅ Dynamically register each app
  microfrontends.forEach(app => {
    registerApplication({
      name: app.name,
      // SystemJS resolves bare specifiers via the import map above; native import() cannot.
      // eslint-disable-next-line no-restricted-properties
      app: () => System.import(app.name),
      // Role-gated apps also need a matching role; RoleRoute re-triggers this once the token is stored
      activeWhen: location =>
        location.pathname.startsWith(app.activeWhen) &&
        hasAnyRole(getRolesFromToken(localStorage.getItem('accessToken')), app.roles),
      customProps: {
        msalInstance: appMsalInstance,
        getToken,
        // tells child app to mount inside #root, not document.body
        domElementGetter: () => {
          const id = `single-spa-application:${app.name}`
          let el = document.getElementById(id)
          if (!el) {
            el = document.createElement('div')
            el.id = id
            // mount inside #root, not body
            document.getElementById('root')!.appendChild(el)
          }
          return el
        },
      },
    })
  })

  const root = ReactDOM.createRoot(document.getElementById('root')!)
  flushSync(() => {
    root.render(
      <React.StrictMode>
        <MsalProvider instance={appMsalInstance}>
          <App />
        </MsalProvider>
      </React.StrictMode>,
    )
  })

  start({ urlRerouteOnly: true })
}
bootstrap()
